import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, FileSignature, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { aiApi } from "./aiApi";
import AISignatureDialog from "./AISignatureDialog";

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-pink-500 border-pink-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{children}</button>
);
const Label = ({ children }) => <div className="text-xs font-bold text-slate-600 mb-1.5">{children}</div>;
const SEL = "w-full h-9 rounded-xl border border-slate-200 bg-white px-2 text-[13px]";
const FORMATS = [["both", "Word + PDF"], ["pdf", "PDF"], ["docx", "Word"]];

export default function AIDocSkillDialog({ skill: openSkill, role, cid, onClose, onCreated }) {
  const last = useRef(openSkill);
  if (openSkill) last.current = openSkill;
  const skill = last.current;
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const isPdf = skill === "pdf_generator";

  useEffect(() => {
    if (!openSkill) return;
    aiApi.docTemplates().then((d) => {
      setMeta(d);
      const first = d.templates[skill]?.[0]?.id;
      setF({ template: first, month: d.month, format: "both", child_id: d.children.length === 1 ? d.children[0].id : "" });
    }).catch(() => toast.error("Gagal memuat templat"));
  }, [openSkill]);

  const tpl = meta?.templates[skill]?.find((t) => t.id === f.template);
  const needs = tpl?.needs || [];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const wantChild = needs.includes("child") || needs.includes("child_or_class");
  const classMode = needs.includes("child_or_class") && role !== "ibu_bapa";
  const ready = tpl && (!needs.includes("instruction") || (f.instruction || "").trim().length >= 5)
    && (!needs.includes("child") || role === "ibu_bapa" || f.child_id) && (!needs.includes("child_or_class") || role === "ibu_bapa" || f.child_id || f.class_name || f.branch);

  const submit = async () => {
    setBusy(true);
    try {
      const d = await aiApi.createDocJob({ ...f, skill, conversation_id: cid });
      onCreated(d); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menjana dokumen"); } finally { setBusy(false); }
  };
  const Icon = isPdf ? FileText : FileSignature;

  return (
    <Dialog open={!!openSkill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[10002] max-w-md rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-doc-skill-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Icon className="h-5 w-5 text-pink-500" /> {isPdf ? "PDF Generator" : "Document Generator"}</DialogTitle></DialogHeader>
        {!meta ? <div className="grid place-items-center h-24"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-3">
            <div><Label>Templat</Label><div className="flex flex-wrap gap-1.5">{(meta.templates[skill] || []).map((t) => (
              <Chip key={t.id} on={f.template === t.id} onClick={() => setF({ ...f, template: t.id })} testid={`ai-doc-tpl-${t.id}`}>{t.label}</Chip>))}</div>
              {tpl && <div className="text-[11px] text-slate-400 mt-1">{tpl.desc}</div>}
            </div>
            {wantChild && (role !== "ibu_bapa" || meta.children.length > 1) ? (
              <div><Label>Kanak-kanak{classMode ? " (atau pilih kelas di bawah)" : ""}</Label>
                <select value={f.child_id || ""} onChange={set("child_id")} className={SEL} data-testid="ai-doc-child-select">
                  <option value="">{classMode ? "— Semua / ikut kelas —" : "— Pilih kanak-kanak —"}</option>
                  {meta.children.map((c) => <option key={c.id} value={c.id}>{c.name}{c.class_name ? ` · ${c.class_name}` : ""}</option>)}
                </select></div>
            ) : null}
            {classMode && !f.child_id && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Cawangan</Label><select value={f.branch || ""} onChange={set("branch")} className={SEL} data-testid="ai-doc-branch-select">
                  <option value="">— Semua —</option>{meta.branches.map((b) => <option key={b} value={b}>{b.replace("Destiny Junior ", "")}</option>)}</select></div>
                <div><Label>Kelas</Label><select value={f.class_name || ""} onChange={set("class_name")} className={SEL} data-testid="ai-doc-class-select">
                  <option value="">— Semua —</option>{meta.classes.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
              </div>
            )}
            {needs.includes("month") && (
              <div><Label>Bulan</Label><input type="month" value={f.month || ""} onChange={set("month")} className={SEL} data-testid="ai-doc-month" /></div>
            )}
            {f.template === "surat_rasmi" && (
              <div><div className="flex items-center justify-between"><Label>Penerima (pilihan)</Label><AISignatureDialog className="mb-1.5" /></div><Textarea rows={2} value={f.recipient || ""} onChange={set("recipient")} placeholder="Cth: Ibu Bapa / Penjaga&#10;Kanak-kanak Destiny Junior" className="rounded-xl text-[13px]" data-testid="ai-doc-recipient" /></div>
            )}
            {(needs.includes("instruction") || f.template === "laporan_anak") && (
              <div><Label>{needs.includes("instruction") ? "Arahan / kandungan" : "Catatan guru (pilihan)"}</Label>
                <Textarea rows={4} value={f.instruction || ""} onChange={set("instruction")} data-testid="ai-doc-instruction" className="rounded-xl text-[13px]"
                  placeholder={f.template === "surat_rasmi" ? "Cth: Jemputan Hari Sukan pada 15 Nov 2026, 8 pagi di padang sekolah" : "Terangkan dokumen yang dikehendaki"} /></div>
            )}
            {!isPdf && (
              <div><Label>Format</Label><div className="flex flex-wrap gap-1.5">{FORMATS.map(([k, l]) => (
                <Chip key={k} on={f.format === k} onClick={() => setF({ ...f, format: k })} testid={`ai-doc-format-${k}`}>{l}</Chip>))}</div></div>
            )}
            <button disabled={busy || !ready} onClick={submit} className="w-full h-11 rounded-2xl bg-gradient-to-r from-pink-500 to-amber-400 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-doc-generate-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? (needs.includes("instruction") ? "Mimi sedang mendraf..." : "Menjana...") : "Jana Dokumen"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
